<template>
  <div class="flow-editor">
    <header class="flow-heading">
      <div><span class="eyebrow">PATIENT INTAKE</span><h2>Questionnaire branches</h2><p>Connect answers to the next question. Start with facelift; publish when the whole journey is ready.</p></div>
      <router-link :to="{ name: 'offer-questions-list' }" class="catalog-link">Question catalog ↗</router-link>
    </header>
    <div class="flow-bar">
      <label>Operation ID <el-input-number v-model="operation" :min="1" :disabled="busy" size="small" /></label>
      <el-button :disabled="busy" size="small" @click="load">Load flow</el-button>
      <span v-if="loaded" class="flow-state" :class="{ live: activeVersion }">{{ activeVersion ? `Live · v${activeVersion}` : 'Draft only' }}</span>
      <span v-if="loaded" class="save-state">{{ dirty ? 'Unsaved changes' : `Saved · revision ${revision}` }}</span>
    </div>
    <p v-if="error" role="alert" class="flow-error">{{ error }}</p>
    <section v-if="loaded">
      <div class="flow-settings">
        <label>Flow languages <el-input v-model="locales" :disabled="busy" size="small" placeholder="en,ua" @input="changed" /></label>
        <label>Preview language <el-select v-model="viewLocale" size="small"><el-option v-for="lang in languageList" :key="lang" :label="lang.toUpperCase()" :value="lang" /></el-select></label>
        <label>Starting question <el-select v-model="draft.entry_node_id" :disabled="busy" size="small" @change="changed"><el-option v-for="node in draft.nodes" :key="node.id" :label="nodeLabel(node)" :value="node.id" /></el-select></label>
        <el-button :disabled="busy || draft.nodes.length >= 12" size="small" icon="el-icon-plus" @click="addNode">Add question</el-button>
      </div>
      <div v-if="graph.issues.length" class="flow-issues" role="status">
        <strong>{{ graph.issues.length }} {{ graph.issues.length === 1 ? 'item' : 'items' }} to review</strong>
        <details><summary>Show details</summary><button v-for="(issue, i) in graph.issues" :key="i" type="button" @click="selectNode(issue.node)">{{ issue.node ? nodeLabel(draft.nodes.find(n => n.id === issue.node)) + ': ' : '' }}{{ issue.text }}</button></details>
      </div>
      <div class="flow-workspace">
        <FlowCanvas :graph="graph" :selected-id="selectedId" :entry-id="draft.entry_node_id" @select="selectNode" />
        <aside class="flow-inspector" aria-label="Branch settings">
          <template v-if="selectedNode">
            <div class="inspector-heading"><span class="eyebrow">QUESTION {{ draft.nodes.indexOf(selectedNode) + 1 }}</span><h3>Branch settings</h3></div>
            <label for="flow-question">Catalog question ID</label>
            <el-input id="flow-question" :value="selectedNode.question_id" :disabled="busy" size="small" @input="changeQuestionId" />
            <el-button class="load-question" size="small" :disabled="busy || !selectedNode.question_id" @click="refreshQuestion(selectedNode)">Load / refresh question</el-button>
            <p v-if="selectedQuestion" class="inspector-question">{{ text(selectedQuestion.title) }}</p>
            <p v-if="selectedQuestion && (selectedQuestion.revision || 0) !== selectedNode.question_revision" class="flow-error">The catalog changed. Refresh and review before saving.</p>
            <label for="flow-answer">Answer or skip</label>
            <el-select id="flow-answer" v-model="selectedAnswer" :disabled="busy" class="full-width" size="small">
              <el-option v-for="answer in selectedQuestion ? selectedQuestion.answers : []" :key="answer.id" :disabled="!answer.id" :label="clip(text(answer.title))" :value="answer.id" />
              <el-option label="Skip this question" value="__skip" />
            </el-select>
            <div class="branch-detail">
              <p class="branch-answer">{{ selectedAnswerObject ? text(selectedAnswerObject.title) : 'Skip this question' }}</p>
              <label for="flow-target">Continue to</label>
              <el-select id="flow-target" :value="selectedTarget" :disabled="busy || !editableBranch" class="full-width" size="small" @change="setTarget">
                <el-option label="✓ Finish & summary" value="finish" />
                <el-option v-for="next in draft.nodes" :key="next.id" :disabled="createsCycle(next.id)" :label="nodeLabel(next) + (createsCycle(next.id) ? ' · would create a loop' : '')" :value="next.id" />
              </el-select>
              <p class="field-hint">Loops are disabled. Several answers can lead to the same question.</p>
              <template v-if="selectedAnswerObject && selectedAnswerObject.id">
                <label v-for="lang in languageList" :key="lang">Patient summary · {{ lang.toUpperCase() }}
                  <el-input :value="summaryValue(lang)" :disabled="busy" type="textarea" :rows="3" placeholder="Optional: what this answer adds to the patient's summary" @input="setSummary(lang, $event)" />
                </label>
              </template>
            </div>
            <button type="button" class="remove-question" :disabled="busy" @click="removeNode(draft.nodes.indexOf(selectedNode))">Remove question</button>
          </template>
          <div v-else class="inspector-empty">Select a question or answer on the map to edit its branch.</div>
        </aside>
      </div>
      <footer class="flow-actions">
        <span>Draft changes do not affect patients until published.</span>
        <el-button size="small" :disabled="busy || graph.issues.length > 0" @click="save">Save draft</el-button>
        <el-button size="small" :disabled="busy || graph.issues.length > 0" @click="preview">Try the journey</el-button>
        <el-button size="small" :disabled="busy || dirty || !revision || graph.issues.length > 0" type="primary" @click="publish">Publish saved draft</el-button>
        <el-button size="small" :disabled="busy || !activeVersion" @click="archive">Disable</el-button>
      </footer>
      <section v-if="snapshot" class="flow-preview">
        <span class="eyebrow">PATIENT PREVIEW · {{ previewLocale.toUpperCase() }}</span>
        <h3>{{ previewNode ? previewNode.question.title[previewLocale] : 'Your summary' }}</h3>
        <el-button v-for="answer in previewNode ? previewNode.question.answers : []" :key="answer.id" @click="previewAnswer(answer.id)">{{ answer.title[previewLocale] }}</el-button>
        <el-button v-if="previewNode" @click="previewSkip">Skip</el-button>
        <p>{{ previewSummary.join(' ') }}</p>
        <el-button size="small" @click="resetPreview">Restart</el-button>
      </section>
    </section>
    <div v-else class="flow-welcome">Load an operation to build or review its questionnaire.</div>
  </div>
</template>
<script>
import flows from '../../api/modules/questionnaire-flows';
import FlowCanvas from '../../components/Questionnaire/FlowCanvas.vue';
import { flowGraph, previewText, localizedText, questionFor, wouldCreateCycle } from '../../components/Questionnaire/flowGraph';
const blank = () => ({ locales: ['en'], entry_node_id: '', nodes: [] });
export default {
  components: { FlowCanvas },
  data: () => ({ operation: 21, loaded: false, busy: false, error: '', revision: 0, activeVersion: null, dirty: false,
    draft: blank(), locales: 'en', questions: {}, snapshot: null, previewId: '', previewSummary: [], requestId: 0, scope: null, selectedId: '', selectedAnswer: '__skip', viewLocale: 'en', previewLocale: 'en', questionRequests: {} }),
  computed: {
    languageList() { return [...new Set(this.locales.split(',').map(l => l.trim()).filter(Boolean))]; },
    graph() { return flowGraph(this.draft, this.questions, this.viewLocale); },
    selectedNode() { return this.draft.nodes.find(n => n.id === this.selectedId); },
    selectedQuestion() { return this.selectedNode && questionFor(this.selectedNode, this.questions); },
    selectedAnswerObject() { return this.selectedQuestion && this.selectedQuestion.answers.find(a => a.id === this.selectedAnswer); },
    editableBranch() { return !!this.selectedNode && (this.selectedAnswer === '__skip' || !!this.selectedAnswerObject); },
    selectedTarget() { return this.selectedNode && (this.selectedAnswer === '__skip' ? this.selectedNode.on_skip : this.selectedNode.transitions[this.selectedAnswer]); },
    previewNode() { return this.snapshot && this.snapshot.nodes.find(n => n.id === this.previewId); },
  },
  watch: {
    languageList(languages) { if (!languages.includes(this.viewLocale)) this.viewLocale = languages[0] || 'en'; },
  },
  methods: {
    clip: previewText,
    text(values) { return localizedText(values, this.viewLocale).text; },
    nodeLabel(node) {
      if (!node) return 'Unknown question';
      const q = questionFor(node, this.questions);
      return `${this.draft.nodes.indexOf(node) + 1}. ${previewText(q ? this.text(q.title) : node.id, 100)}`;
    },
    selectNode(id, answerId) {
      if (!this.draft.nodes.some(n => n.id === id)) return;
      this.selectedId = id; this.selectedAnswer = answerId || '__skip';
    },
    changeQuestionId(id) {
      const node = this.selectedNode;
      node.question_id = id; this.$delete(this.questions, node.id); this.selectedAnswer = '__skip'; this.changed();
    },
    createsCycle(target) { return !!this.selectedNode && wouldCreateCycle(this.draft, this.selectedId, target); },
    setTarget(target) {
      if (this.busy || !this.editableBranch) return;
      if (target !== 'finish' && !this.draft.nodes.some(n => n.id === target)) return;
      if (this.createsCycle(target)) { this.error = 'This transition would create a loop. Choose another destination.'; return; }
      if (this.selectedAnswer === '__skip') this.selectedNode.on_skip = target;
      else this.$set(this.selectedNode.transitions, this.selectedAnswer, target);
      this.error = ''; this.changed();
    },
    summaryValue(lang) { return this.selectedNode.summary_fragments[this.selectedAnswer] && this.selectedNode.summary_fragments[this.selectedAnswer][lang] || ''; },
    setSummary(lang, value) {
      if (this.busy || !this.selectedAnswerObject) return;
      const fragments = this.selectedNode.summary_fragments;
      if (!fragments[this.selectedAnswer]) this.$set(fragments, this.selectedAnswer, {});
      this.$set(fragments[this.selectedAnswer], lang, value); this.changed();
    },
    async refreshQuestion(node) {
      if (this.busy) return;
      this.busy = true; this.error = '';
      try { await this.loadQuestion(node, true); } finally { this.busy = false; }
    },
    changed() { this.dirty = true; this.snapshot = null; },
    apply(row) {
      this.revision = row ? row.revision : 0; this.activeVersion = row ? row.active_version : null;
      this.draft = row ? JSON.parse(JSON.stringify(row.draft)) : blank(); this.locales = this.draft.locales.join(',');
      this.questions = {}; this.snapshot = null; this.dirty = false; this.loaded = true; this.questionRequests = {};
      this.selectedId = this.draft.entry_node_id || ''; this.selectedAnswer = '__skip'; this.viewLocale = this.draft.locales[0] || 'en';
    },
    async load() {
      if (this.busy) return;
      if (this.dirty && !await this.$confirm('Discard unsaved changes?').then(() => true).catch(() => false)) return;
      this.busy = true; this.error = ''; const scope = this.operation; const request = ++this.requestId;
      try { const r = await flows.get(scope); if (request !== this.requestId) return; this.scope = scope; this.apply(r.data.flow);
        for (const node of this.draft.nodes) await this.loadQuestion(node, false);
      } catch (e) { this.error = this.message(e); } finally { this.busy = false; }
    },
    async loadQuestion(node, update = true) {
      const id = node.question_id; const request = this.requestId;
      const questionRequest = (this.questionRequests[node.id] || 0) + 1; this.questionRequests[node.id] = questionRequest;
      try {
        const response = await flows.getQuestion(id); const q = response.data.question || response.data;
        if (request !== this.requestId || this.questionRequests[node.id] !== questionRequest || node.question_id !== id || !this.draft.nodes.includes(node)) return;
        if (q.id !== id) throw new Error('Question response identity mismatch');
        this.$set(this.questions, node.id, q);
        if (update) { node.question_revision = q.revision || 0; this.changed(); }
        const transitions = {}, fragments = {};
        for (const answer of q.answers || []) if (answer.id) {
          transitions[answer.id] = node.transitions[answer.id] || 'finish';
          fragments[answer.id] = { ...(node.summary_fragments[answer.id] || {}) };
          for (const lang of this.languageList) if (!(lang in fragments[answer.id])) this.$set(fragments[answer.id], lang, '');
        }
        if (update) { this.$set(node, 'transitions', transitions); this.$set(node, 'summary_fragments', fragments); }
      } catch (e) { if (request === this.requestId && this.questionRequests[node.id] === questionRequest && node.question_id === id) this.error = this.message(e); }
    },
    addNode() {
      if (this.busy || this.draft.nodes.length >= 12) return;
      let n = 1; while (this.draft.nodes.some(x => x.id === `question_${n}`)) n++;
      const id = `question_${n}`;
      this.draft.nodes.push({ id, question_id: '', question_revision: 0, transitions: {}, on_skip: 'finish', summary_fragments: {} });
      if (!this.draft.entry_node_id) this.draft.entry_node_id = id; this.selectNode(id); this.changed();
    },
    async removeNode(index) {
      if (this.busy || index < 0) return;
      const node = this.draft.nodes[index];
      const incoming = this.draft.nodes.some(n => n.id !== node.id && (n.on_skip === node.id || Object.values(n.transitions).includes(node.id)));
      if (incoming || (this.draft.entry_node_id === node.id && this.draft.nodes.length > 1)) {
        this.error = 'Choose another starting question and redirect incoming branches before removing this question.'; return;
      }
      if (!await this.$confirm('Remove this question from the draft? The catalog question will remain.').then(() => true).catch(() => false)) return;
      const currentIndex = this.draft.nodes.indexOf(node); if (currentIndex < 0 || this.busy) return;
      this.draft.nodes.splice(currentIndex, 1); this.$delete(this.questions, node.id);
      if (this.draft.entry_node_id === node.id) this.draft.entry_node_id = '';
      this.selectedId = this.draft.entry_node_id; this.selectedAnswer = '__skip'; this.changed();
    },
    payload() {
      const draft = JSON.parse(JSON.stringify(this.draft)); draft.locales = this.languageList;
      for (const node of draft.nodes) for (const id of Object.keys(node.summary_fragments)) {
        if (Object.values(node.summary_fragments[id]).every(v => !String(v).trim())) delete node.summary_fragments[id];
      }
      return draft;
    },
    async run(fn) {
      if (this.busy || this.operation !== this.scope) { this.error = 'Load the selected operation first.'; return; }
      this.busy = true; this.error = '';
      try { await fn(); } catch (e) { this.error = this.message(e); } finally { this.busy = false; }
    },
    async save() { if (this.graph.issues.length) { this.error = 'Resolve the highlighted issues before saving.'; return; } await this.run(async () => { const r = await flows.save(this.scope, this.revision, this.payload()); this.revision = r.data.revision; this.dirty = false; }); },
    async preview() { if (this.graph.issues.length) { this.error = 'Resolve the highlighted issues before previewing.'; return; } await this.run(async () => { const r = await flows.action(this.scope, 'preview', { draft: this.payload() }); this.snapshot = r.data; this.previewLocale = this.viewLocale; this.resetPreview(); }); },
    async publish() {
      if (this.dirty || this.busy || this.graph.issues.length) return;
      if (!await this.$confirm('Publish this version for new site sessions?').then(() => true).catch(() => false)) return;
      if (this.dirty || this.busy || this.graph.issues.length) return;
      await this.run(async () => { const r = await flows.action(this.scope, 'publish', { revision: this.revision }); this.revision = r.data.revision; this.activeVersion = r.data.active_version; });
    },
    async archive() { await this.run(async () => { const r = await flows.action(this.scope, 'archive', { revision: this.revision }); this.revision = r.data.revision; this.activeVersion = null; }); },
    resetPreview() { this.previewId = this.snapshot.entry_node_id; this.previewSummary = []; },
    previewAnswer(id) {
      const node = this.previewNode; const text = node.summary_fragments[id] && node.summary_fragments[id][this.previewLocale];
      if (text) this.previewSummary.push(text); this.previewId = node.transitions[id];
    },
    previewSkip() { this.previewId = this.previewNode.on_skip; },
    message(e) {
      if (e.response && e.response.status === 409) return 'Changed on server. Reload before saving or publishing.';
      const issues = e.response && e.response.data && e.response.data.issues;
      if (Array.isArray(issues)) return issues.slice(0, 5).map(i => `${i.path || '/'}: ${i.message || i.code}`).join(' · ');
      return 'Could not complete the action. Check question IDs, revisions, languages and all transitions.';
    },
  },
  beforeDestroy() { this.requestId++; },
};
</script>
<style scoped>
.flow-editor { padding: 24px; color: #253750; }
.flow-heading { display: flex; justify-content: space-between; gap: 24px; align-items: center; margin-bottom: 24px; }
.flow-heading h2 { font-size: 27px; color: #203249; margin: 6px 0; font-weight: 600; }
.flow-heading p, .save-state { color: #77869b; font-size: 13px; margin: 0; }
.eyebrow { color: #6b7e99; font-size: 10px; font-weight: 700; letter-spacing: 1.4px; }
.catalog-link { font-size: 13px; color: #3d6dca; white-space: nowrap; }
.flow-bar, .flow-settings { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding: 16px 20px; background: white; border: 1px solid #dbe4ef; border-radius: 12px; }
.flow-bar label { display: flex; align-items: center; gap: 10px; margin: 0; font-size: 12px; }
.flow-state { padding: 5px 10px; border-radius: 20px; background: #edf2fa; color: #58708f; font-size: 11px; font-weight: 600; }
.flow-state.live { background: #e0f4e9; color: #1b7650; }
.save-state { margin-left: auto; }
.flow-settings { background: transparent; border: none; padding: 20px 0; align-items: flex-end; }
.flow-settings label { width: 150px; margin: 0; font-size: 11px; color: #627795; }
.flow-settings label:nth-child(3) { width: 280px; }
.flow-settings label > div { margin-top: 6px; width: 100%; }
.flow-settings > button { margin-left: auto; }
.flow-workspace { display: grid; grid-template-columns: minmax(0, 1fr) 330px; gap: 20px; align-items: start; }
.flow-inspector { padding: 20px; border: 1px solid #dbe4ef; border-radius: 14px; background: #fff; max-height: 780px; overflow: auto; }
.inspector-heading h3 { color: #203249; font-size: 19px; margin: 5px 0 18px; }
.flow-inspector label { display: block; font-size: 11px; color: #627795; margin: 14px 0 7px; }
.load-question { width: 100%; margin-top: 8px; }
.inspector-question { font-weight: 600; font-size: 13px; margin: 18px 0; overflow-wrap: anywhere; }
.full-width { width: 100%; }
.branch-detail { margin-top: 20px; border-top: 1px solid #e7edf5; padding-top: 12px; }
.branch-answer { color: #3b629d; font-size: 13px; overflow-wrap: anywhere; }
.field-hint { font-size: 11px; color: #7d8ba1; margin-top: 8px; line-height: 1.5; }
.remove-question { margin-top: 24px; color: #b74444; background: none; border: none; font: inherit; font-size: 12px; cursor: pointer; padding: 0; }
.inspector-empty, .flow-welcome { padding: 40px 15px; text-align: center; color: #78879b; font-size: 13px; }
.flow-error { background: #fff1ed; border-radius: 8px; padding: 12px 16px; color: #ae442c; font-size: 13px; }
.flow-issues { background: #fff7e9; border: 1px solid #f0dfbd; border-radius: 10px; padding: 12px 16px; color: #8d5d12; margin-bottom: 16px; font-size: 12px; display: flex; gap: 18px; }
.flow-issues summary { cursor: pointer; }
.flow-issues button { display: block; background: none; border: none; color: inherit; text-align: left; cursor: pointer; margin: 8px 0; font-size: 12px; }
.flow-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 20px 0; }
.flow-actions > span { flex: 1; color: #77869b; font-size: 12px; min-width: 180px; }
.flow-actions button { margin-left: 0; }
.flow-preview { padding: 28px; background: #fff; border: 1px solid #dbe4ef; border-radius: 14px; }
.flow-preview h3 { color: #203249; margin: 12px 0 20px; }
.flow-preview .el-button { white-space: normal; margin: 5px; }
.flow-preview p { margin: 20px 0; }
@media (max-width: 1150px) { .flow-workspace { grid-template-columns: minmax(0, 1fr); } .flow-inspector { max-height: none; } }
@media (max-width: 600px) { .flow-editor { padding: 12px; } .flow-heading { display: block; } .flow-heading h2 { font-size: 22px; } .catalog-link { display: inline-block; margin-top: 12px; } }
</style>
